<template>
    <div class="admin-oss-manager animation-fadeIn">
        <div class="page-header">
            <div class="header-info">
                <h2 class="page-title">OSS 资源管理</h2>
                <p class="page-subtitle">管理存储在阿里云 OSS 中的图片和附件，监控资源引用情况，清理无用资产</p>
            </div>
            <div class="header-actions">
                <el-button @click="fetchFiles" :loading="loading" class="modern-btn">
                    <el-icon>
                        <Refresh />
                    </el-icon> 刷新数据
                </el-button>
                <el-button type="primary" @click="handleUploadClick" class="modern-btn">
                    <el-icon>
                        <UploadFilled />
                    </el-icon> 上传资源
                </el-button>
                <el-button type="danger" @click="handleBatchDelete" :disabled="!selectedFiles.length"
                    class="modern-btn">
                    <el-icon>
                        <Delete />
                    </el-icon> 批量删除 ({{ selectedFiles.length }})
                </el-button>
            </div>
        </div>

        <!-- Filter Bar -->
        <el-card shadow="never" class="filter-card">
            <el-row :gutter="20" align="middle">
                <el-col :span="8">
                    <el-input v-model="searchQuery" placeholder="搜索文件名..." prefix-icon="Search" clearable />
                </el-col>
                <el-col :span="6">
                    <el-select v-model="usageFilter" placeholder="使用状态" clearable style="width: 100%">
                        <el-option label="全部" value="all" />
                        <el-option label="已使用" value="used" />
                        <el-option label="未使用" value="unused" />
                    </el-select>
                </el-col>
                <el-col :span="10" class="stat-group">
                    <div class="stat-item">
                        <span class="label">总文件:</span>
                        <span class="value">{{ filteredFiles.length }}</span>
                    </div>
                    <div class="stat-item danger">
                        <span class="label">无引用:</span>
                        <span class="value">{{ unusedCount }}</span>
                    </div>
                </el-col>
            </el-row>
        </el-card>

        <!-- Main Table -->
        <el-card shadow="never" class="table-card">
            <el-table :data="filteredFiles" style="width: 100%" @selection-change="handleSelectionChange"
                v-loading="loading" row-class-name="modern-row" header-row-class-name="modern-header">
                <el-table-column type="selection" width="55" />

                <el-table-column label="资源预览" width="100">
                    <template #default="{ row }">
                        <el-image v-if="isImage(row.name)" :src="getFileUrl(row.name)" lazy fit="cover"
                            class="resource-preview" @click.stop="handlePreview(row)">
                            <template #error>
                                <div class="image-slot"><el-icon>
                                        <Picture />
                                    </el-icon></div>
                            </template>
                        </el-image>
                        <div v-else class="file-icon-box previewable" @click="handlePreview(row)">
                            <el-icon>
                                <Document />
                            </el-icon>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="资源名称" min-width="200" show-overflow-tooltip>
                    <template #default="{ row }">
                        <div class="file-name-info">
                            <span class="fname clickable" @click="handlePreview(row)">{{ row.name }}</span>
                            <el-link :href="getFileUrl(row.name)" target="_blank" type="primary" :underline="false"
                                class="copy-btn">
                                <el-icon>
                                    <Link />
                                </el-icon>
                            </el-link>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="大小" width="100">
                    <template #default="{ row }">{{ formatFileSize(row.size) }}</template>
                </el-table-column>

                <el-table-column label="引用次数" width="120" align="center">
                    <template #default="{ row }">
                        <el-tooltip v-if="row.usageCount > 0" placement="top">
                            <template #content>
                                <div class="usage-list">
                                    <div v-for="(use, idx) in row.usedBy" :key="idx" class="usage-item">{{ use }}</div>
                                </div>
                            </template>
                            <el-tag :type="row.usageCount > 0 ? 'success' : 'info'" class="usage-tag">
                                {{ row.usageCount }} 次引用
                            </el-tag>
                        </el-tooltip>
                        <el-tag v-else type="info" class="usage-tag">未使用</el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="更新时间" width="180">
                    <template #default="{ row }">{{ row.lastModified }}</template>
                </el-table-column>

                <el-table-column label="操作" width="120" fixed="right">
                    <template #default="{ row }">
                        <el-button type="primary" link @click="handlePreview(row)">预览</el-button>
                        <el-popconfirm :title="row.usageCount > 0 ? '该资源正在被引用，确定要删除吗？' : '确认删除该资源？'"
                            @confirm="deleteFile(row)" confirm-button-text="确定" cancel-button-text="取消"
                            confirm-button-type="danger">
                            <template #reference>
                                <el-button type="danger" link>删除</el-button>
                            </template>
                        </el-popconfirm>
                    </template>
                </el-table-column>
            </el-table>
        </el-card>

        <!-- Preview Dialog -->
        <el-dialog v-model="previewVisible" :title="previewTitle" width="70%" destroy-on-close center
            class="modern-dialog">
            <div class="preview-container">
                <template v-if="previewType === 'image'">
                    <el-image :src="previewUrl" fit="contain" style="width: 100%; max-height: 70vh;" />
                </template>
                <template v-else-if="previewType === 'iframe'">
                    <iframe :src="previewUrl" width="100%" height="700px" frameborder="0"></iframe>
                </template>
                <template v-else>
                    <div class="unsupported-preview">
                        <el-empty description="暂不支持该格式在线预览">
                            <el-button type="primary" @click="downloadFile">直接下载</el-button>
                        </el-empty>
                    </div>
                </template>
            </div>
            <template #footer>
                <span class="dialog-footer">
                    <el-button @click="downloadFile">下载文件</el-button>
                    <el-button type="primary" @click="previewVisible = false">关闭</el-button>
                </span>
            </template>
        </el-dialog>
        <!-- Upload Dialog -->
        <el-dialog v-model="uploadVisible" title="上传资源" width="500px" center class="modern-dialog">
            <div class="upload-container">
                <el-upload class="upload-demo" drag action="" :auto-upload="false" :on-change="handleFileChange"
                    :limit="1" :show-file-list="false">
                    <el-icon class="el-icon--upload"><upload-filled /></el-icon>
                    <div class="el-upload__text">
                        将文件拖到此处，或 <em>点击上传</em>
                    </div>
                </el-upload>
                <div v-if="uploadFile" class="selected-file">
                    <el-icon>
                        <Document />
                    </el-icon>
                    <span class="file-name">{{ uploadFile.name }}</span>
                    <el-icon class="remove-icon" @click="uploadFile = null">
                        <Close />
                    </el-icon>
                </div>
            </div>
            <template #footer>
                <span class="dialog-footer">
                    <el-button @click="uploadVisible = false">取消</el-button>
                    <el-button type="primary" @click="submitUpload" :loading="uploading" :disabled="!uploadFile">
                        开始上传
                    </el-button>
                </span>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
    Refresh, Delete, Search, Picture, Document,
    Link, QuestionFilled, UploadFilled, Close
} from '@element-plus/icons-vue'
import api, { uploadOSSFile } from '@/utils/api'

const loading = ref(false)
const list = ref([])
const searchQuery = ref('')
const usageFilter = ref('all')
const selectedFiles = ref([])

// Preview state
const previewVisible = ref(false)
const previewTitle = ref('')
const previewUrl = ref('')
const previewType = ref('') // image, iframe, unsupported
const currentFile = ref(null)

// Upload state
const uploadVisible = ref(false)
const uploading = ref(false)
const uploadFile = ref(null)

const handleUploadClick = () => {
    uploadVisible.value = true
    uploadFile.value = null
}

const handleFileChange = (file) => {
    uploadFile.value = file
}

const submitUpload = async () => {
    if (!uploadFile.value) return

    uploading.value = true
    try {
        const formData = new FormData()
        formData.append('file', uploadFile.value.raw)

        const res = await uploadOSSFile(formData)
        if (res.data.success) {
            ElMessage.success('上传成功')
            uploadVisible.value = false
            fetchFiles()
        } else {
            ElMessage.error(res.data.error || '上传失败')
        }
    } catch (err) {
        ElMessage.error('上传出错')
    } finally {
        uploading.value = false
    }
}

const fetchFiles = async () => {
    loading.value = true
    try {
        const res = await api.get('/api/admin/oss/files')
        if (res.data.success) {
            list.value = res.data.files
        } else {
            ElMessage.error(res.data.error || '获取失败')
        }
    } catch (err) {
        ElMessage.error('服务连接异常')
    } finally {
        loading.value = false
    }
}

const filteredFiles = computed(() => {
    return list.value.filter(item => {
        const matchesSearch = item.name.toLowerCase().includes(searchQuery.value.toLowerCase())
        let matchesUsage = true
        if (usageFilter.value === 'used') matchesUsage = item.usageCount > 0
        if (usageFilter.value === 'unused') matchesUsage = item.usageCount === 0
        return matchesSearch && matchesUsage
    })
})

const unusedCount = computed(() => {
    return list.value.filter(item => item.usageCount === 0).length
})

const isImage = (filename) => {
    const ext = filename.split('.').pop().toLowerCase()
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
}

const getFileUrl = (name) => {
    return `https://question-bank-images.oss-cn-hangzhou.aliyuncs.com/${name}`
}

const handlePreview = (row) => {
    currentFile.value = row
    previewTitle.value = `预览: ${row.name}`
    const url = getFileUrl(row.name)
    const ext = row.name.split('.').pop().toLowerCase()

    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
        previewType.value = 'image'
        previewUrl.value = url
    } else if (ext === 'pdf') {
        previewType.value = 'iframe'
        // Using Google Docs Viewer for PDF or direct if CORS allows
        previewUrl.value = `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(url)}`
    } else if (['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt'].includes(ext)) {
        previewType.value = 'iframe'
        previewUrl.value = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
    } else {
        previewType.value = 'unsupported'
        previewUrl.value = url
    }

    previewVisible.value = true
}

const downloadFile = () => {
    if (currentFile.value) {
        window.open(getFileUrl(currentFile.value.name), '_blank')
    }
}

const formatFileSize = (bytes) => {
    if (!bytes) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const handleSelectionChange = (val) => {
    selectedFiles.value = val
}

const deleteFile = async (row) => {
    try {
        const res = await api.post('/api/admin/oss/delete', { fileName: row.name })
        if (res.data.success) {
            ElMessage.success('已删除')
            fetchFiles()
        }
    } catch (err) {
        ElMessage.error('删除失败')
    }
}

const handleBatchDelete = async () => {
    const names = selectedFiles.value.map(f => f.name)
    const referenced = selectedFiles.value.filter(f => f.usageCount > 0)

    let warningMsg = `确认删除选中的 ${names.length} 个资源？`
    if (referenced.length > 0) {
        warningMsg += ` 其中有 ${referenced.length} 个资源正在被引用，删除可能导致内容显示异常！`
    }

    try {
        await ElMessageBox.confirm(warningMsg, '批量删除确认', {
            confirmButtonText: '确定删除',
            cancelButtonText: '取消',
            type: 'warning'
        })

        loading.value = true
        const res = await api.post('/api/admin/oss/batch-delete', { fileNames: names })
        if (res.data.success) {
            ElMessage.success(`成功删除 ${res.data.deleted.length} 个文件`)
            fetchFiles()
        }
    } catch (err) {
        if (err !== 'cancel') ElMessage.error('删除操作失败')
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    fetchFiles()
})
</script>

<style scoped>
.admin-oss-manager {
    max-width: 1200px;
    margin: 0 auto;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
}

.page-title {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    color: #1a1a1a;
}

.page-subtitle {
    margin-top: 4px;
    color: #666;
    font-size: 14px;
}

.header-actions {
    display: flex;
    gap: 12px;
}

.filter-card {
    margin-bottom: 20px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
}

.stat-group {
    display: flex;
    justify-content: flex-end;
    gap: 24px;
}

.stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.stat-item .label {
    font-size: 12px;
    color: #909399;
}

.stat-item .value {
    font-size: 20px;
    font-weight: 700;
    color: #303133;
}

.stat-item.danger .value {
    color: #f56c6c;
}

.table-card {
    border-radius: 12px;
    overflow: hidden;
}

.resource-preview {
    width: 60px;
    height: 60px;
    border-radius: 8px;
    background: #f5f7fa;
    display: block;
    cursor: pointer;
    transition: transform 0.2s;
}

.resource-preview:hover {
    transform: scale(1.05);
}

.image-slot {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    font-size: 24px;
    color: #909399;
}

.file-icon-box {
    width: 60px;
    height: 60px;
    border-radius: 8px;
    background: #f5f7fa;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 32px;
    color: #909399;
}

.file-icon-box.previewable {
    cursor: pointer;
    transition: all 0.2s;
}

.file-icon-box.previewable:hover {
    background: #ecf5ff;
    color: #409eff;
}

.fname.clickable {
    cursor: pointer;
    transition: color 0.2s;
}

.fname.clickable:hover {
    color: #409eff;
}

.preview-container {
    min-height: 400px;
    display: flex;
    justify-content: center;
    align-items: center;
}

.unsupported-preview {
    padding: 40px;
}

.file-name-info {
    display: flex;
    align-items: center;
    gap: 8px;
}

.fname {
    font-weight: 500;
    word-break: break-all;
}

.usage-tag {
    cursor: pointer;
    border-radius: 100px;
    padding: 0 12px;
}

.usage-list {
    max-width: 300px;
    padding: 8px 0;
}

.usage-item {
    padding: 4px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    font-size: 12px;
}

.usage-item:last-child {
    border-bottom: none;
}

.animation-fadeIn {
    animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

:deep(.modern-header th) {
    background-color: #f8fafc !important;
    font-weight: 600;
    color: #606266;
}

:deep(.modern-row:hover td) {
    background-color: #f0f7ff !important;
}

.modern-btn {
    border-radius: 8px;
}

.upload-container {
    padding: 20px;
    text-align: center;
}

.selected-file {
    margin-top: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px;
    background: #f5f7fa;
    border-radius: 6px;
}

.file-name {
    font-weight: 500;
}

.remove-icon {
    cursor: pointer;
    color: #909399;
}

.remove-icon:hover {
    color: #f56c6c;
}
</style>
